import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import debounce from 'lodash.debounce';
import { useCallback, useMemo } from 'react';

import DashboardFilterCard from './DashboardFilterCard';

interface SelectOptionType {
  label: string;
  value: any;
}

interface DashboardAutocompleteFilterCardProps {
  title: string;
  autocompleteProps: {
    disabled?: boolean;
    filters?: {
      field: string;
      operator: string;
    };
    options?: SelectOptionType[];
    setFilter?: (updater: (prev: { items: any[] }) => { items: any[] }) => void;
  };
}

const DashboardAutoCompleteFilterCard = ({
  title,
  autocompleteProps,
}: DashboardAutocompleteFilterCardProps) => {
  const {
    disabled = false,
    filters,
    options = [],
    setFilter,
  } = autocompleteProps;

  const debouncedSetParentFilter = useMemo(
    () =>
      setFilter && filters
        ? debounce((currentSelection: SelectOptionType[]) => {
            setFilter((prev) => {
              const otherItems = prev.items.filter(
                (item) => item.field !== filters.field,
              );
              let newItem = null;
              if (currentSelection.length > 0) {
                newItem = {
                  ...filters,
                  id: Math.random(),
                  value: currentSelection.map((item) => item.value),
                };
              }

              return { items: newItem ? [...otherItems, newItem] : otherItems };
            });
          }, 750)
        : undefined,
    [setFilter, filters],
  );

  const onChange = useCallback(
    (currentSelection: SelectOptionType[]) =>
      debouncedSetParentFilter?.(currentSelection),
    [debouncedSetParentFilter],
  );

  return (
    <DashboardFilterCard title={title}>
      <Autocomplete
        disabled={disabled}
        multiple
        options={options}
        defaultValue={[]}
        onChange={(_, newSelection: SelectOptionType[]) => {
          onChange(newSelection);
        }}
        disableCloseOnSelect
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        renderOption={(props, option, { selected }) => (
          <MenuItem {...props} key={option.value} value={option.value} dense>
            <Checkbox checked={selected} size="small" />
            <Typography
              variant="body2"
              sx={{ display: 'inline-block', whiteSpace: 'pre-line' }}
            >
              {option.label}
            </Typography>
          </MenuItem>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select..."
            size="small"
            sx={{
              '.MuiInputBase-root': {
                maxHeight: '40px',
              },
            }}
          />
        )}
        renderValue={(tagValues, getTagProps) => (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              maxWidth: '100%',
              '&::-webkit-scrollbar': {
                height: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,.2)',
                borderRadius: '3px',
              },
            }}
          >
            {tagValues.map((option, index) => {
              return (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value ?? `chip-${index}`}
                  label={option.label}
                  size="small"
                  sx={{
                    flexShrink: 0,
                    mr: 0.5,
                  }}
                />
              );
            })}
          </Box>
        )}
        sx={{
          '.MuiInputBase-root': {
            flexWrap: 'nowrap',
            overflow: 'hidden',
          },
        }}
      />
    </DashboardFilterCard>
  );
};

export default DashboardAutoCompleteFilterCard;
